<template>
  <div class="cms-page">
    <el-card shadow="never">
      <div slot="header" class="card-header">
        <span>{{ pageTitle }}</span>
        <div class="header-actions">
          <el-button size="small" icon="el-icon-folder" @click="openCategoryDialog()">分类管理</el-button>
          <el-button type="primary" size="small" icon="el-icon-plus" @click="openArticleDialog()">
            {{ addButtonText }}
          </el-button>
        </div>
      </div>

      <div class="category-tabs">
        <el-button
          v-for="tab in categoryTabs"
          :key="tab.key"
          size="small"
          :type="activeCategoryKey === tab.key ? 'primary' : 'default'"
          round
          @click="selectCategoryTab(tab)"
        >{{ tab.label }}</el-button>
      </div>

      <div class="toolbar">
        <el-input
          v-model="titleKeyword"
          placeholder="搜索标题..."
          clearable
          class="toolbar-search"
          @keyup.enter.native="handleSearch"
        >
          <el-button slot="append" icon="el-icon-search" @click="handleSearch" />
        </el-input>
      </div>

      <el-table v-loading="loading" :data="list" stripe class="cms-table">
        <el-table-column prop="id" label="编号" width="70" />
        <el-table-column v-if="showCoverColumn" :label="cmsType === 1 ? '缩略图' : '封面'" width="90">
          <template slot-scope="{ row }">
            <div class="cover-thumb">
              <img v-if="row.coverUrl" :src="row.coverUrl" alt="">
              <i v-else class="el-icon-picture-outline" />
            </div>
          </template>
        </el-table-column>
        <el-table-column label="标题" min-width="180" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.title || '-' }}</template>
        </el-table-column>
        <el-table-column label="分类" width="120">
          <template slot-scope="{ row }">
            <el-tag size="mini" type="info">{{ row.categoryName || '-' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column v-if="cmsType === 2" label="价格" width="90">
          <template slot-scope="{ row }">
            <span class="price">¥{{ row.price || 0 }}</span>
          </template>
        </el-table-column>
        <el-table-column v-if="cmsType === 4" label="价格" width="90">
          <template slot-scope="{ row }">
            <span class="price">¥{{ row.price || 0 }}</span>
          </template>
        </el-table-column>
        <el-table-column v-if="cmsType === 2 || cmsType === 4" label="佣金金额" width="100">
          <template slot-scope="{ row }">
            <span class="price">¥{{ row.commissionAmount || 0 }}</span>
          </template>
        </el-table-column>
        <el-table-column v-if="cmsType === 2 || cmsType === 4" label="有效时长" width="100">
          <template slot-scope="{ row }">
            <span>{{ row.validityDays ? row.validityDays + '天' : '永久' }}</span>
          </template>
        </el-table-column>
        <el-table-column v-if="cmsType === 2" label="讲师" width="110">
          <template slot-scope="{ row }">{{ row.author || '-' }}</template>
        </el-table-column>
        <el-table-column v-if="cmsType === 2" label="章节数" width="90">
          <template slot-scope="{ row }">{{ row.chapterCount || 0 }}</template>
        </el-table-column>
        <el-table-column v-if="cmsType === 4" label="文件格式" width="100">
          <template slot-scope="{ row }">{{ row.fileFormat || '-' }}</template>
        </el-table-column>
        <el-table-column v-if="cmsType === 4" label="下载次数" width="100">
          <template slot-scope="{ row }">{{ row.downloadCount || 0 }}</template>
        </el-table-column>
        <el-table-column v-if="cmsType === 1" label="作者" width="110">
          <template slot-scope="{ row }">{{ row.author || '管理员' }}</template>
        </el-table-column>
        <el-table-column label="发布时间" width="120">
          <template slot-scope="{ row }">{{ formatUserDate(row.publishTime || row.createdAt) }}</template>
        </el-table-column>
        <el-table-column label="状态" width="90">
          <template slot-scope="{ row }">
            <el-tag size="small" :type="row.status === 1 ? 'success' : 'warning'">
              {{ labelOf(CMS_ARTICLE_STATUS, row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" :width="cmsType === 4 ? 220 : 180" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" size="small" @click="openArticleDialog(row)">编辑</el-button>
            <el-button v-if="cmsType === 2" type="text" size="small" @click="openArticleDialog(row)">查看详情</el-button>
            <el-button v-if="cmsType === 4" type="text" size="small" class="muted" @click="resetDownloads(row)">重置下载</el-button>
            <el-button type="text" size="small" class="danger" @click="handleDeleteArticle(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="table-footer">共 {{ total }} 条记录</div>

      <el-pagination
        class="pagination"
        background
        layout="prev, pager, next"
        :current-page.sync="page"
        :page-size="pageSize"
        :total="total"
        @current-change="loadList"
      />
    </el-card>

    <el-dialog
      :title="articleForm.id ? editDialogTitle : addDialogTitle"
      :visible.sync="articleVisible"
      :width="cmsType === 2 ? '1100px' : cmsType === 4 ? '900px' : '960px'"
      top="4vh"
      custom-class="article-dialog"
      @closed="handleArticleDialogClosed"
    >
      <div v-loading="articleLoading">
      <el-form ref="articleForm" :model="articleForm" :rules="articleRules" label-width="90px">
        <el-form-item label="分类" prop="categoryId">
          <el-select v-model="articleForm.categoryId" style="width: 100%">
            <el-option v-for="cat in categories" :key="cat.id" :label="cat.name" :value="cat.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="标题" prop="title">
          <el-input v-model="articleForm.title" />
        </el-form-item>
        <el-form-item v-if="cmsType === 1 || cmsType === 2" :label="cmsType === 2 ? '讲师' : '作者'">
          <el-input v-model="articleForm.author" />
        </el-form-item>
        <el-form-item v-if="cmsType === 4" label="文件格式">
          <el-select v-model="articleForm.fileFormat" style="width: 100%">
            <el-option label="PDF" value="PDF" />
            <el-option label="PPTX" value="PPTX" />
            <el-option label="DOCX" value="DOCX" />
            <el-option label="ZIP" value="ZIP" />
            <el-option label="MP4" value="MP4" />
          </el-select>
        </el-form-item>
        <el-form-item :label="cmsType === 2 ? '课程简介' : cmsType === 4 ? '材料简介' : '摘要'">
          <el-input v-model="articleForm.summary" type="textarea" :rows="2" />
        </el-form-item>
        <el-form-item :label="cmsType === 2 ? '课程详情' : cmsType === 4 ? '材料介绍' : '内容'" prop="content">
          <rich-text-editor
            v-if="articleVisible"
            v-model="articleForm.content"
            biz-type="cms"
            :height="cmsType === 2 ? '280px' : cmsType === 4 ? '240px' : '420px'"
          />
        </el-form-item>
        <tutorial-article-form v-if="cmsType === 2" :form="articleForm" />
        <training-material-form v-if="cmsType === 4" :form="articleForm" />
        <el-form-item label="封面">
          <image-upload v-model="articleForm.coverUrl" biz-type="cms_cover" button-text="上传封面" />
        </el-form-item>
        <el-form-item v-if="cmsType === 2 || cmsType === 4" label="价格">
          <el-input-number v-model="articleForm.price" :min="0" :precision="2" style="width: 100%" />
        </el-form-item>
        <el-form-item v-if="cmsType === 2 || cmsType === 4" label="原价">
          <el-input-number v-model="articleForm.originalPrice" :min="0" :precision="2" style="width: 100%" />
          <div class="form-tip">选填；门户展示划线价，不填则不显示原价</div>
        </el-form-item>
        <el-form-item v-if="cmsType === 2 || cmsType === 4" label="佣金金额">
          <el-input-number v-model="articleForm.commissionAmount" :min="0" :precision="2" style="width: 100%" />
          <div class="form-tip">用户购买后，该金额将作为佣金结算给用户的代理商</div>
        </el-form-item>
        <el-form-item v-if="cmsType === 2 || cmsType === 4" label="有效时长(天)">
          <el-input-number v-model="articleForm.validityDays" :min="0" :step="1" placeholder="0 表示永久有效" style="width: 100%" />
          <div class="form-tip">购买后开始计时，超过天数后需再次购买；0 或留空表示永久有效</div>
        </el-form-item>
        <el-form-item label="状态">
          <el-select v-model="articleForm.status" style="width: 100%">
            <el-option v-for="opt in articleStatusOptions" :key="opt.value" :label="opt.label" :value="opt.value" />
          </el-select>
        </el-form-item>
      </el-form>
      </div>
      <div slot="footer">
        <el-button @click="articleVisible = false">取消</el-button>
        <el-button type="primary" :loading="submitting" @click="submitArticle">保存</el-button>
      </div>
    </el-dialog>

    <el-dialog title="分类管理" :visible.sync="categoryManageVisible" width="560px" @opened="handleCategoryManageOpened">
      <div class="category-manage-toolbar">
        <el-button type="primary" size="small" icon="el-icon-plus" @click="openCategoryForm()">新增分类</el-button>
      </div>
      <el-table
        v-if="categoryManageVisible"
        ref="categoryTable"
        v-loading="categoryLoading"
        :data="categories"
        stripe
        size="small"
        max-height="360"
        empty-text="暂无分类"
      >
        <el-table-column prop="name" label="名称" min-width="120" />
        <el-table-column prop="sortOrder" label="排序" width="70" />
        <el-table-column label="状态" width="80">
          <template slot-scope="{ row }">{{ labelOf(COMMON_STATUS, row.status) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="120" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" size="small" @click="openCategoryForm(row)">编辑</el-button>
            <el-button type="text" size="small" class="danger" @click="handleDeleteCategory(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-dialog>

    <el-dialog :title="categoryForm.id ? '编辑分类' : '新增分类'" :visible.sync="categoryFormVisible" width="420px" append-to-body>
      <el-form ref="categoryForm" :model="categoryForm" :rules="categoryRules" label-width="80px">
        <el-form-item label="名称" prop="name">
          <el-input v-model="categoryForm.name" />
        </el-form-item>
        <el-form-item label="排序">
          <el-input-number v-model="categoryForm.sortOrder" :min="0" />
        </el-form-item>
        <el-form-item label="状态">
          <el-select v-model="categoryForm.status" style="width: 100%">
            <el-option v-for="opt in categoryStatusOptions" :key="opt.value" :label="opt.label" :value="opt.value" />
          </el-select>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="categoryFormVisible = false">取消</el-button>
        <el-button type="primary" :loading="submitting" @click="submitCategory">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  getCmsCategories,
  getCmsArticles,
  getCmsArticleDetail,
  saveCmsArticle,
  deleteCmsArticle,
  saveCmsCategory,
  deleteCmsCategory,
  resetCmsArticleDownloads
} from '@/api/admin'
import ImageUpload from '@/components/admin/ImageUpload.vue'
import RichTextEditor from '@/components/admin/RichTextEditor.vue'
import TutorialArticleForm from '@/components/admin/TutorialArticleForm.vue'
import TrainingMaterialForm from '@/components/admin/TrainingMaterialForm.vue'
import { CMS_ARTICLE_STATUS, COMMON_STATUS, labelOf, optionsOf } from '@/utils/adminConstants'
import { formatUserDate } from '@/utils/userFormat'
import {
  parseChaptersJson,
  parseFaqsJson,
  serializeChapters,
  serializeFaqs,
  defaultTutorialForm
} from '@/utils/tutorialFormat'
import { parseMaterialCatalogJson, serializeMaterialCatalog, defaultMaterialForm } from '@/utils/materialFormat'
import listPage from '@/mixins/listPage'

const CMS_TYPE_LABELS = {
  1: { title: '资讯管理', add: '发布资讯', edit: '编辑资讯' },
  2: { title: '教程管理', add: '添加教程', edit: '编辑教程' },
  4: { title: '培训材料管理', add: '新增培训材料', edit: '编辑培训材料' }
}

export default {
  name: 'CmsArticles',
  components: { ImageUpload, RichTextEditor, TutorialArticleForm, TrainingMaterialForm },
  mixins: [listPage],
  data() {
    return {
      CMS_ARTICLE_STATUS,
      COMMON_STATUS,
      categories: [],
      categoryLoading: false,
      categoryId: null,
      activeCategoryKey: 'all',
      titleKeyword: '',
      articleVisible: false,
      articleLoading: false,
      categoryManageVisible: false,
      categoryFormVisible: false,
      submitting: false,
      articleForm: {
        id: null,
        categoryId: null,
        title: '',
        summary: '',
        content: '',
        coverUrl: '',
        author: '',
        instructorName: '',
        instructorAvatar: '',
        instructorTitle: '',
        instructorIntro: '',
        targetAudience: '',
        learningGoals: '',
        chapters: [],
        faqs: [],
        chapterCount: 0,
        fileFormat: 'PDF',
        price: 0,
        originalPrice: 0,
        commissionAmount: 0,
        validityDays: 0,
        status: 1
      },
      categoryForm: { name: '', type: null, sortOrder: 0, status: 1 },
      articleRules: {
        categoryId: [{ required: true, message: '请选择分类', trigger: 'change' }],
        title: [{ required: true, message: '请输入标题', trigger: 'blur' }],
        content: [{ required: true, message: '请输入正文内容', trigger: 'change' }]
      },
      categoryRules: {
        name: [{ required: true, message: '请输入名称', trigger: 'blur' }]
      }
    }
  },
  computed: {
    cmsType() {
      return this.$route.meta.cmsType || 1
    },
    pageTitle() {
      return (CMS_TYPE_LABELS[this.cmsType] || CMS_TYPE_LABELS[1]).title
    },
    addButtonText() {
      return (CMS_TYPE_LABELS[this.cmsType] || CMS_TYPE_LABELS[1]).add
    },
    editDialogTitle() {
      return (CMS_TYPE_LABELS[this.cmsType] || CMS_TYPE_LABELS[1]).edit
    },
    addDialogTitle() {
      return this.addButtonText
    },
    showCoverColumn() {
      // 资讯 / 课程 / 培训材料列表均展示封面缩略图
      return this.cmsType === 1 || this.cmsType === 2 || this.cmsType === 4
    },
    articleStatusOptions() {
      return optionsOf(CMS_ARTICLE_STATUS)
    },
    categoryStatusOptions() {
      return optionsOf(COMMON_STATUS)
    },
    categoryTabs() {
      return [{ key: 'all', label: '全部', id: null }].concat(
        this.categories
          .filter(cat => cat.status === 1)
          .map(cat => ({ key: String(cat.id), label: cat.name, id: cat.id }))
      )
    }
  },
  watch: {
    '$route.meta.cmsType'() {
      this.resetFilters()
      this.loadCategories()
      this.loadList()
    }
  },
  created() {
    this.loadCategories()
    this.loadList()
  },
  methods: {
    labelOf,
    formatUserDate,
    resetFilters() {
      this.categoryId = null
      this.activeCategoryKey = 'all'
      this.titleKeyword = ''
      this.page = 1
    },
    selectCategoryTab(tab) {
      this.activeCategoryKey = tab.key
      this.categoryId = tab.id
      this.handleSearch()
    },
    async loadCategories() {
      this.categoryLoading = true
      try {
        this.categories = await getCmsCategories(this.cmsType) || []
        if (this.categoryId && !this.categories.some(cat => cat.id === this.categoryId)) {
          this.resetFilters()
        }
      } catch (e) {
      } finally {
        this.categoryLoading = false
      }
    },
    fetchList(params) {
      return getCmsArticles({
        ...params,
        cmsType: this.cmsType,
        categoryId: this.categoryId || undefined,
        title: this.titleKeyword || undefined
      })
    },
    openArticleDialog(row) {
      if (row && row.id) {
        this.loadArticleDetail(row.id)
      } else {
        this.articleForm = this.buildEmptyArticleForm()
        this.articleVisible = true
      }
    },
    buildEmptyArticleForm() {
      const base = {
        id: null,
        categoryId: this.categoryId || this.categories[0]?.id || null,
        title: '',
        summary: '',
        content: '',
        coverUrl: '',
        author: '',
        chapterCount: 0,
        fileFormat: 'PDF',
        price: 0,
        originalPrice: 0,
        commissionAmount: 0,
        validityDays: 0,
        status: 1
      }
      if (this.cmsType === 2) {
        return { ...base, ...defaultTutorialForm() }
      }
      if (this.cmsType === 4) {
        return { ...base, ...defaultMaterialForm() }
      }
      return base
    },
    mapArticleDetail(data) {
      const form = {
        ...data,
        price: data.price || 0,
        originalPrice: data.originalPrice || 0,
        commissionAmount: data.commissionAmount || 0,
        validityDays: data.validityDays || 0,
        content: data.content || '',
        author: data.author || '',
        fileFormat: data.fileFormat || 'PDF'
      }
      if (this.cmsType === 2) {
        form.instructorIntro = data.instructorIntro || ''
        form.targetAudience = data.targetAudience || ''
        form.learningGoals = data.learningGoals || ''
        form.chapters = parseChaptersJson(data.chaptersJson)
        form.faqs = parseFaqsJson(data.faqsJson)
        form.chapterCount = form.chapters.length
      }
      if (this.cmsType === 4) {
        form.learningGoals = data.learningGoals || ''
        form.targetAudience = data.targetAudience || ''
        form.catalogChapters = parseMaterialCatalogJson(data.materialCatalog)
        form.materialFileUrl = data.materialFileUrl || ''
        form.chapterCount = data.chapterCount || 0
      }
      return form
    },
    async loadArticleDetail(id) {
      this.articleLoading = true
      try {
        const detail = await getCmsArticleDetail(id)
        this.articleForm = this.mapArticleDetail(detail)
        this.articleVisible = true
      } catch (e) {
        this.$message.error(e.message || '加载详情失败，请刷新后重试')
      } finally {
        this.articleLoading = false
      }
    },
    handleArticleDialogClosed() {
      this.articleForm = this.buildEmptyArticleForm()
    },
    openCategoryDialog() {
      this.categoryManageVisible = true
      this.loadCategories()
    },
    handleCategoryManageOpened() {
      this.$nextTick(() => {
        if (this.$refs.categoryTable) {
          this.$refs.categoryTable.doLayout()
        }
      })
    },
    openCategoryForm(row) {
      this.categoryForm = row
        ? { ...row }
        : { id: null, name: '', type: this.cmsType, sortOrder: 0, status: 1 }
      this.categoryFormVisible = true
    },
    buildArticlePayload() {
      const payload = { ...this.articleForm }
      if (this.cmsType === 2) {
        payload.chaptersJson = serializeChapters(this.articleForm.chapters)
        payload.faqsJson = serializeFaqs(this.articleForm.faqs)
        payload.chapterCount = this.articleForm.chapters.length
        delete payload.chapters
        delete payload.faqs
      }
      if (this.cmsType === 4) {
        payload.materialCatalog = serializeMaterialCatalog(this.articleForm.catalogChapters)
        delete payload.catalogChapters
      }
      return payload
    },
    submitArticle() {
      this.$refs.articleForm.validate(async valid => {
        if (!valid) return
        const plainText = (this.articleForm.content || '').replace(/<[^>]+>/g, '').trim()
        if (!plainText) {
          const msg = this.cmsType === 2 ? '请输入课程详情' : this.cmsType === 4 ? '请输入材料介绍' : '请输入正文内容'
          this.$message.warning(msg)
          return
        }
        if (this.cmsType === 2) {
          const invalidChapter = (this.articleForm.chapters || []).find(
            ch => (ch.duration || ch.videoUrl || ch.description) && !(ch.title || '').trim()
          )
          if (invalidChapter) {
            this.$message.warning('已填写内容的章节需填写标题')
            return
          }
        }
        this.submitting = true
        try {
          await saveCmsArticle(this.buildArticlePayload())
          this.$message.success('保存成功')
          this.articleVisible = false
          this.loadList()
        } catch (e) {
        } finally {
          this.submitting = false
        }
      })
    },
    submitCategory() {
      this.$refs.categoryForm.validate(async valid => {
        if (!valid) return
        this.submitting = true
        try {
          await saveCmsCategory(this.categoryForm)
          this.$message.success('分类已保存')
          this.categoryFormVisible = false
          await this.loadCategories()
          this.loadList()
        } catch (e) {
        } finally {
          this.submitting = false
        }
      })
    },
    handleDeleteCategory(row) {
      this.$confirm(`确定删除分类「${row.name}」？`, '提示', { type: 'warning' })
        .then(async () => {
          await deleteCmsCategory(row.id)
          this.$message.success('已删除')
          if (this.categoryId === row.id) {
            this.resetFilters()
          }
          await this.loadCategories()
          this.loadList()
        }).catch(() => {})
    },
    resetDownloads(row) {
      this.$confirm(`确定重置「${row.title}」的下载次数？`, '提示', { type: 'warning' })
        .then(async () => {
          await resetCmsArticleDownloads(row.id)
          this.$message.success('已重置')
          this.loadList()
        }).catch(() => {})
    },
    handleDeleteArticle(row) {
      this.$confirm('确定删除该内容？', '提示', { type: 'warning' }).then(async () => {
        try {
          await deleteCmsArticle(row.id)
          this.$message.success('已删除')
          this.loadList()
        } catch (e) {
        }
      }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
}
.header-actions {
  display: flex;
  gap: 8px;
}
.category-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}
.toolbar {
  margin-bottom: 16px;
}
.toolbar-search {
  width: 260px;
}
.cover-thumb {
  width: 56px;
  height: 56px;
  border-radius: 6px;
  background: #f5f7fa;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  color: #c0c4cc;
  font-size: 20px;
}
.cover-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.price {
  color: #e6a23c;
  font-weight: 500;
}
.table-footer {
  margin-top: 12px;
  font-size: 13px;
  color: #909399;
}
.pagination {
  margin-top: 12px;
  text-align: right;
}
.danger {
  color: #f56c6c;
}
.muted {
  color: #909399;
}
.category-manage-toolbar {
  margin-bottom: 12px;
  text-align: right;
}
.form-tip {
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
  margin-top: 4px;
}
</style>

<style>
.article-dialog .el-dialog__body {
  max-height: calc(90vh - 120px);
  overflow-y: auto;
}
</style>
